import type { ComponentProps } from 'react';
import React, { createContext, useContext, useLayoutEffect, useRef, useState } from 'react';

import { cn } from '@change/ds-utils';

type PromptInputContextType = {
	isLoading: boolean;
	value: string;
	setValue: (value: string) => void;
	maxHeight: number | string;
	disabled?: boolean;
	textareaRef: React.RefObject<HTMLTextAreaElement | null>;
};

const PromptInputContext = createContext<PromptInputContextType>({
	isLoading: false,
	value: '',
	setValue: () => {},
	maxHeight: 240,
	disabled: false,
	textareaRef: { current: null },
});

function usePromptInput() {
	return useContext(PromptInputContext);
}

export type PromptInputProps = {
	isLoading?: boolean;
	value?: string;
	onValueChange?: (value: string) => void;
	maxHeight?: number | string;
	children: React.ReactNode;
	className?: string;
	disabled?: boolean;
} & React.ComponentProps<'div'>;

const PromptInput: React.FC<PromptInputProps> = ({
	className,
	isLoading = false,
	maxHeight = 240,
	value,
	onValueChange,
	children,
	disabled = false,
	onClick,
	...props
}) => {
	const [internalValue, setInternalValue] = useState(value || '');
	const textareaRef = useRef<HTMLTextAreaElement>(null);

	const handleChange = (newValue: string) => {
		setInternalValue(newValue);
		onValueChange?.(newValue);
	};

	return (
		<PromptInputContext.Provider
			data-qa="prompt-input"
			value={{
				isLoading,
				value: value ?? internalValue,
				setValue: onValueChange ?? handleChange,
				maxHeight,
				disabled,
				textareaRef,
			}}
		>
			<div
				data-qa="prompt-input-wrapper"
				data-testid="prompt-input-wrapper"
				className={cn(
					'rounded-large border-stroke-strong bg-background-base elevation-elevated relative flex overflow-hidden border',
					'has-[textarea:focus]:outline-stroke-focus has-[textarea:focus]:border-transparent has-[textarea:focus]:outline has-[textarea:focus]:outline-2',
					className,
				)}
				{...props}
			>
				{children}
			</div>
		</PromptInputContext.Provider>
	);
};

const Textarea = ({ className, ref, ...props }: ComponentProps<'textarea'>) => {
	return (
		<textarea ref={ref} data-qa="prompt-input-textarea" data-slot="textarea" className={cn('', className)} {...props} />
	);
};

export type PromptInputTextareaProps = {
	disableAutosize?: boolean;
} & React.ComponentProps<typeof Textarea>;

const PromptInputTextarea: React.FC<PromptInputTextareaProps> = ({
	className,
	onKeyDown,
	disableAutosize = false,
	onChange,
	...props
}) => {
	const { value, setValue, maxHeight, disabled, textareaRef } = usePromptInput();

	const adjustHeight = (el: HTMLTextAreaElement | null) => {
		if (!el || disableAutosize) return;

		// eslint-disable-next-line no-param-reassign
		el.style.height = 'auto';

		if (typeof maxHeight === 'number') {
			// eslint-disable-next-line no-param-reassign
			el.style.height = `${Math.min(el.scrollHeight, maxHeight)}px`;
		} else {
			// eslint-disable-next-line no-param-reassign
			el.style.height = `min(${el.scrollHeight}px, ${maxHeight})`;
		}
	};

	const handleRef = (el: HTMLTextAreaElement | null) => {
		textareaRef.current = el;
		adjustHeight(el);
	};

	useLayoutEffect(() => {
		if (!textareaRef.current || disableAutosize) return;

		const el = textareaRef.current;
		el.style.height = 'auto';

		if (typeof maxHeight === 'number') {
			el.style.height = `${Math.min(el.scrollHeight, maxHeight)}px`;
		} else {
			el.style.height = `min(${el.scrollHeight}px, ${maxHeight})`;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [value, maxHeight, disableAutosize]);

	const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
		adjustHeight(e.target);
		setValue(e.target.value);
		onChange?.(e);
	};

	const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
		if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
			e.preventDefault();
			const { form } = e.currentTarget;
			if (form) {
				// requestSubmit landed in Safari/iOS Safari 16; the es2015 bundle targets >= 12.
				if (typeof form.requestSubmit === 'function') {
					form.requestSubmit();
				} else {
					form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
				}
			}
		}
		onKeyDown?.(e);
	};

	return (
		<Textarea
			data-qa="prompt-input-textarea"
			data-testid="prompt-input-textarea"
			ref={handleRef}
			value={value}
			onChange={handleChange}
			onKeyDown={handleKeyDown}
			className={cn(
				'text-size-body min-h-[44px] w-full resize-none border-none px-5 pt-6 pb-16 shadow-none outline-none focus-visible:ring-0 focus-visible:ring-offset-0',
				className,
			)}
			rows={1}
			disabled={disabled}
			{...props}
		/>
	);
};

export type PromptInputActionsProps = React.HTMLAttributes<HTMLDivElement>;

const PromptInputActions: React.FC<PromptInputActionsProps> = ({ children, className, ...props }) => {
	return (
		<div
			data-testid="prompt-input-actions"
			className={cn('absolute right-3 bottom-3 flex items-center gap-2', className)}
			{...props}
		>
			{children}
		</div>
	);
};

export { PromptInput, PromptInputTextarea, PromptInputActions };
